import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { applyGrantDiff, diffGrants } from "./validation/grantContent";

type WriteTx = Pick<Transaction<Schema>, "run" | "mutate">;

// The tenant admins are exactly the user-principal `tenant_admin` grants at the
// tenant scope. A group-conferred grant (one carrying a `userGroupId`) is never
// an admin, so both the SCIM read path and the reconcile below share this one
// predicate rather than each restating the user-principal filter.
export function tenantAdminUserGrants(tenantId: string) {
  return zql.roleGrants
    .where("tenantId", tenantId)
    .where("builtinRoleId", "tenant_admin")
    .where("scopeId", tenantId)
    .where("userId", "IS NOT", null);
}

export function activeTenantAdminUserGrants(
  tenantId: string,
  excludedUserId?: string
) {
  return tenantAdminUserGrants(tenantId).whereExists("user", (user) => {
    const activeUsers = user.where("deactivated", false);
    return excludedUserId
      ? activeUsers.where("id", "!=", excludedUserId)
      : activeUsers;
  });
}

// Reconciles the tenant admins to exactly `desiredUsers`, requiring at least
// one active user so a tenant can never be left without an active admin.
export async function reconcileTenantAdminGrants(
  tx: WriteTx,
  desiredUsers: readonly { id: string; deactivated: boolean | null }[],
  tenantId: string
): Promise<void> {
  if (desiredUsers.length === 0) {
    throw userFacingError("There must be at least one active tenant admin");
  }
  if (desiredUsers.some((user) => user.deactivated === true)) {
    throw userFacingError("Tenant admins must be active");
  }
  const desiredUserIds = desiredUsers.map((user) => user.id);
  const existing = await tx.run(tenantAdminUserGrants(tenantId));
  const diff = diffGrants(
    existing,
    desiredUserIds.map((userId) => ({ userId, builtinRoleId: "tenant_admin" }))
  );
  await applyGrantDiff(tx, tenantId, diff, tenantId);
}
